/**
 * A centred dialog over the designer: a dark card on a dimmed backdrop.
 * Escape (capture phase) and the X close it; so does a click on the backdrop
 * unless `dismissable` is false (a form that must be finished or cancelled by
 * its own buttons).
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useEscape } from './useEscape';
import './designerHeader.css';

export interface DesignerModalProps {
  title: string;
  children: React.ReactNode;
  /** Close (Escape, the X, the backdrop). Absent: no X, and Escape does nothing. */
  onClose?: () => void;
  closeLabel?: string;
  width?: number;
  /** Whether a click on the backdrop closes it (default true). */
  dismissable?: boolean;
  /** Something next to the title (an icon). */
  icon?: React.ReactNode;
}

export const DesignerModal: React.FC<DesignerModalProps> = ({ title, children, onClose, closeLabel = 'Close', width = 380, dismissable = true, icon }) => {
  useEscape(!!onClose, () => onClose?.());
  return createPortal(
    <div
      className="dh-modal-backdrop"
      onPointerDown={(e) => { if (dismissable && onClose && e.target === e.currentTarget) onClose(); }}
    >
      <div role="dialog" aria-modal="true" aria-label={title} className="dh-modal" style={{ width }} onPointerDown={(e) => e.stopPropagation()}>
        <div className="dh-modal-head">
          {icon}
          <h2 className="dh-modal-title">{title}</h2>
          {onClose && (
            <button type="button" className="dh-icon-btn" onClick={onClose} aria-label={closeLabel} title={closeLabel}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="dh-modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default DesignerModal;
